/** Emails — v1's per-property Emails tab as a collapsible drawer section.
 * Loads on open (not on drawer mount) with v1's 60-second result cache; the
 * Refresh button always forces a reload. Strict/loose match split, lazy
 * message bodies, account picker, and the full compose modal — with Send
 * disabled until the live-fire session (Phase B). */
import { useEffect, useRef, useState } from "react";
import {
  MAIL_MSG, agentEmailOf, buildLeadMailQuery, logOnly, mailContacts, pickAccount,
  sellerEmailOf, splitStrict, type MailAccount, type MailMsg, type MailSendPayload,
} from "./api";
import { liveOn } from "../../lib/liveFire";
import { newIdemKey } from "../../lib/idem";

export interface MailLoaders {
  accounts: () => Promise<MailAccount[]>;
  threads: (accountId: string, q: string) => Promise<{ messages: MailMsg[]; reconnect: boolean }>;
  log: (leadKey: string) => Promise<MailMsg[]>;
  body: (accountId: string, id: string) => Promise<string>;
  userId: () => string | null;
  /** live-fire (Phase B): resolves only on server-confirmed send, else throws */
  send: (p: MailSendPayload) => Promise<void>;
}

interface LoadedState {
  accs: MailAccount[];
  accId: string | null;
  msgs: MailMsg[];
  log: MailMsg[];
  sellerUsed: boolean;
  reconnect: boolean;
  at: number;
}

/** v1's 60s cache, keyed by address — survives drawer close/reopen. */
const cache = new Map<string, LoadedState>();

export function EmailsSection({
  address,
  fields,
  loaders,
  startOpen = false,
}: {
  address: string;
  fields: Record<string, unknown> | undefined;
  loaders: MailLoaders;
  startOpen?: boolean;
}) {
  const [state, setState] = useState<LoadedState | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [showLoose, setShowLoose] = useState(false);
  const [openBodies, setOpenBodies] = useState<Record<string, string | null>>({}); // id → body|null(loading)
  const [composeOpen, setComposeOpen] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => {
    alive.current = false;
  }, []);

  const seller = sellerEmailOf(fields);

  const load = async (force: boolean, accOverride?: string) => {
    if (busy) return;
    const cached = cache.get(address);
    if (!force && !accOverride && cached && Date.now() - cached.at < 60000) {
      setState(cached);
      return;
    }
    setBusy(true);
    setNote(MAIL_MSG.loading);
    try {
      const accs = cached?.accs.length ? cached.accs : await loaders.accounts();
      if (!accs.length) {
        const empty: LoadedState = { accs: [], accId: null, msgs: [], log: [], sellerUsed: false, reconnect: false, at: Date.now() };
        cache.set(address, empty);
        if (alive.current) {
          setState(empty);
          setNote(null);
        }
        return;
      }
      const accId = accOverride ?? cached?.accId ?? pickAccount(accs, loaders.userId())?.id ?? null;
      const accEmail = accs.find((a) => a.id === accId)?.email ?? "";
      const { q, sellerUsed } = buildLeadMailQuery(address, seller, accEmail);
      // Settled, not all: the logged-email read and the live thread search fail
      // independently, and losing both because one timed out is worse than
      // showing what did load. fetchLeadLog now throws instead of returning []
      // — an empty list and a failed read are different answers, and the wrong
      // one tells you a conversation doesn't exist.
      const [tmR, lgR] = await Promise.allSettled([
        accId ? loaders.threads(accId, q) : Promise.resolve({ messages: [], reconnect: false }),
        loaders.log(address),
      ]);
      if (tmR.status === "rejected" && lgR.status === "rejected") throw tmR.reason;
      const tm = tmR.status === "fulfilled" ? tmR.value : { messages: [], reconnect: false };
      const lg = lgR.status === "fulfilled" ? lgR.value : [];
      const next: LoadedState = { accs, accId, msgs: tm.messages, log: lg, sellerUsed, reconnect: tm.reconnect, at: Date.now() };
      // Don't cache a partial read as if it were the whole picture.
      if (tmR.status === "fulfilled" && lgR.status === "fulfilled") cache.set(address, next);
      if (alive.current) {
        setState(next);
        setNote(
          lgR.status === "rejected" ? "Couldn't load emails already logged against this property — showing live threads only."
          : tmR.status === "rejected" ? "Couldn't search the mailbox — showing previously logged emails only."
          : null,
        );
      }
    } catch {
      if (alive.current) setNote("Could not reach the backend");
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  // Always-open now: fire the initial load once on mount (was the toggle-open trigger).
  useEffect(() => {
    if (!state) void load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBody = (m: MailMsg) => {
    if (m.id in openBodies) {
      setOpenBodies((p) => {
        const n = { ...p };
        delete n[m.id];
        return n;
      });
      return;
    }
    setOpenBodies((p) => ({ ...p, [m.id]: null }));
    const accId = state?.accId;
    if (m.fromLog || !accId) {
      setOpenBodies((p) => ({ ...p, [m.id]: m.snippet || "(no content)" }));
      return;
    }
    loaders
      .body(accId, m.id)
      .then((b) => alive.current && setOpenBodies((p) => (m.id in p ? { ...p, [m.id]: b } : p)))
      .catch(() => alive.current && setOpenBodies((p) => (m.id in p ? { ...p, [m.id]: MAIL_MSG.bodyFail } : p)));
  };

  const split = state ? splitStrict(state.msgs, address, state.sellerUsed, seller) : { direct: [], loose: [] };
  const extraLog = state ? logOnly(state.log, state.msgs) : [];
  const accEmail = state?.accs.find((a) => a.id === state.accId)?.email ?? "";
  const contacts = state ? mailContacts(state.msgs, seller, agentEmailOf(fields), accEmail) : [];
  const street = (address.split(",")[0] ?? "").trim();

  const row = (m: MailMsg) => (
    <div key={(m.fromLog ? "log:" : "gm:") + m.id} className="mailrow">
      <button className="mailrowbtn" onClick={() => toggleBody(m)}>
        <div className="mailtop">
          <span className="mailsubj">{m.subject || "(no subject)"}</span>
          <span className={"cmppill " + (m.direction === "out" ? "cmp-active" : "cmp-sold")}>{m.direction === "out" ? "Sent" : "Received"}</span>
          {m.fromLog ? <span className="cmppill mail-log">CRM log</span> : null}
          <span className="maildate">{m.date}</span>
        </div>
        <div className="mailmeta">{m.from} → {m.to}</div>
        <div className="mailsnip">{m.snippet}</div>
      </button>
      {m.id in openBodies ? <div className="mailbody">{openBodies[m.id] ?? "Loading…"}</div> : null}
    </div>
  );

  return (
    <div className="detailsec open">
      <div className="sechead static">
        <span>✉️ Emails</span>
        <span className="sub">
          {state ? `${split.direct.length + extraLog.length} for this property · ` : ""}
        </span>
      </div>
        <div className="secbody mailsec">
          <div className="mailhead">
            <span className="sub">EMAILS — {street.toUpperCase()}</span>
            <div className="mailacts">
              {state && state.accs.length > 1 ? (
                <select value={state.accId ?? ""} onChange={(e) => void load(true, e.target.value)}>
                  {state.accs.map((a) => (
                    <option key={a.id} value={a.id}>{a.email}</option>
                  ))}
                </select>
              ) : null}
              <button className="btn slim" disabled={busy} onClick={() => void load(true)} title="Refresh">⟳</button>
              <button className="btn slim primary" disabled={!state || !state.accs.length} onClick={() => setComposeOpen(true)}>✎ Compose</button>
            </div>
          </div>
          {note ? <div className="cmpnote">{note}</div> : null}
          {state?.reconnect ? <div className="cmpnote mailwarn">{MAIL_MSG.reconnect}</div> : null}
          {state ? (
            state.accs.length === 0 ? (
              <div className="arvempty">
                <div className="sub">{MAIL_MSG.noMailbox}</div>
                <button className="btn" disabled title={MAIL_MSG.connectPhaseB}>Connect Gmail — Phase B</button>
              </div>
            ) : (
              <>
                <div className="mailhint sub">
                  {state.sellerUsed
                    ? `Matching: street address or seller (${seller})`
                    : seller
                      ? "Seller email is your own mailbox address — matching by street address only."
                      : "Matching: street address. Add a seller email (Details → Seller Contact) to also match their messages."}
                </div>
                <div className="maillist">
                  {split.direct.map(row)}
                  {extraLog.map(row)}
                </div>
                {split.loose.length && !showLoose ? (
                  <button className="mailloosebtn" onClick={() => setShowLoose(true)}>
                    Show {split.loose.length} more match{split.loose.length > 1 ? "es" : ""} — address only in attachments / quoted text
                  </button>
                ) : null}
                {showLoose ? <div className="maillist">{split.loose.map(row)}</div> : null}
                {!split.direct.length && !extraLog.length && !split.loose.length && !busy ? (
                  <div className="arvempty"><span className="sub">{MAIL_MSG.empty}</span></div>
                ) : null}
              </>
            )
          ) : null}
        </div>
      {composeOpen && state ? (
        <ComposeModal
          address={address}
          accounts={state.accs}
          accId={state.accId}
          seller={seller}
          accEmail={accEmail}
          contacts={contacts}
          onSend={loaders.send}
          onClose={() => setComposeOpen(false)}
        />
      ) : null}
    </div>
  );
}

/** v1 leadMailCompose. Send is live-fire (Phase B) and HONEST: it locks the
 * button for the single attempt (no double-send), shows the server's error and
 * keeps the draft on failure, and only closes on a confirmed send. */
function ComposeModal({
  address,
  accounts,
  accId,
  seller,
  accEmail,
  contacts,
  onSend,
  onClose,
}: {
  address: string;
  accounts: MailAccount[];
  accId: string | null;
  seller: string;
  accEmail: string;
  contacts: [string, string][];
  onSend: (p: MailSendPayload) => Promise<void>;
  onClose: () => void;
}) {
  const defTo = seller && seller.toLowerCase() !== accEmail.toLowerCase() ? seller : contacts[0]?.[0] ?? "";
  const [from, setFrom] = useState(accId ?? "");
  const [to, setTo] = useState(defTo);
  const [subject, setSubject] = useState(address);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canSend = liveOn("emailSend");
  const idemRef = useRef<string | null>(null); // per-draft send key (idempotency)

  const doSend = async () => {
    if (sending || sent) return; // single-flight: no double-send
    if (!to.trim()) { setErr(MAIL_MSG.sendNeedsTo); return; }
    setSending(true);
    setErr(null);
    if (!idemRef.current) idemRef.current = newIdemKey();
    try {
      await onSend({ accountId: from, to: to.trim(), subject, body, leadKey: address, idemKey: idemRef.current });
      setSent(true); // honest: only after the server confirmed
      setTimeout(onClose, 700);
    } catch (e) {
      // loud + keep the draft — the v1 green-lie must never recur
      setErr((e as { detail?: string; message?: string })?.detail ?? (e as Error)?.message ?? "Send failed — not sent. Your draft is kept.");
    } finally {
      setSending(false);
    }
  };
  useEffect(() => {
    // capture phase + stopPropagation: Escape closes THIS modal only — the
    // drawer's own Escape handler (a window bubble listener) must not fire
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener("keydown", esc, true);
    return () => window.removeEventListener("keydown", esc, true);
  }, [onClose]);
  return (
    <div className="cmpmodalscrim" onClick={onClose}>
      <div className="cmpmodal mailcompose" onClick={(e) => e.stopPropagation()}>
        <div className="cmpmodalbody wide">
          <div className="cmpmodalhead">
            <h3>✉️ Email — {(address.split(",")[0] ?? "").trim()}</h3>
            <button className="close" onClick={onClose}>×</button>
          </div>
          <div className="mailform">
            <div className="cmpfrow">
              <div>
                <label className="lbl">From</label>
                <select value={from} onChange={(e) => setFrom(e.target.value)}>
                  {accounts.length ? (
                    accounts.map((a) => <option key={a.id} value={a.id}>{a.email}</option>)
                  ) : (
                    <option value="">No mailbox connected</option>
                  )}
                </select>
              </div>
              <div>
                <label className="lbl">To</label>
                <input list="mail-to-list" value={to} placeholder="recipient@example.com" onChange={(e) => setTo(e.target.value)} />
                <datalist id="mail-to-list">
                  {contacts.map(([em, label]) => (
                    <option key={em} value={em}>{label}</option>
                  ))}
                </datalist>
                {contacts.length ? (
                  <div className="mailchips">
                    {contacts.slice(0, 4).map(([em, label]) => (
                      <button key={em} className="pillbtn on-src" title={em} onClick={() => setTo(em)}>
                        {label || em.split("@")[0]}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            <label className="lbl">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} />
            <label className="lbl">Message</label>
            <textarea rows={7} value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          {err ? <div className="mailsenderr">{err}</div> : null}
          <div className="cmpmodalacts">
            <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
            {canSend ? (
              <button className="btn primary" disabled={sending || sent || !to.trim()} onClick={() => void doSend()}>
                {sent ? MAIL_MSG.sentOk : sending ? MAIL_MSG.sending : "Send"}
              </button>
            ) : (
              <button className="btn primary" disabled title={MAIL_MSG.sendPhaseB}>
                Send — Phase B
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
